import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Table, Popconfirm, message, notification } from "antd";
import { DeleteOutlined, EyeOutlined, SendOutlined } from "@ant-design/icons";
import moment from "moment";
import request from "helper/request";
import RoutesHeader from "modules/client/routes/components/header/RoutesHeader";
import { shortId, convertArrayToObject, convertObjectToArray } from "helper/util";
import { ProfileContext } from "layouts/private/ProfileContext";
import { selectCurrentUser } from "../../../auth/store/authSlice";
import {
  doOptimizeOrder,
  doOptimizeOrderCallback,
  doStoreDraftId
} from "../../active-orders/store/thunks";
import {
  selectOrderState,
  saveAsDraftNewOrder,
  STATE_STATUSES,
  selectDraftId
} from "../../active-orders/store/orderSlice";
import {
  dataJobTypeOptimize,
  handleRoutesOptimize,
  deleteShiftStart
} from "../../active-orders/helper";
import { getVehicleTypeList } from "../../vehicle-types/store/thunks";
import { selectAllVehicleTypes } from "../../vehicle-types/store/slice";
import "./PlanListPage.scss";

const PlanListPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const profileCtx = useContext(ProfileContext);
  const user = useSelector(selectCurrentUser);
  const orderState = useSelector(selectOrderState);
  const draftIdSelector = useSelector(selectDraftId);
  const vehicleCategoryList = useSelector(selectAllVehicleTypes);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingPlanId, setSubmittingPlanId] = useState(null);

  const breadCrumbs = [{ text: "PLANS" }];

  useEffect(() => {
    fetchPlans();
    dispatch(getVehicleTypeList());
    // eslint-disable-next-line
  }, []);

  // Note: no result polling is needed here!
  // The ConfirmRoutePage (/orders/new) already polls for the result (useRouteJobResult).
  // It listens to: `/optimizing-ordering-completed/${user.sub},${draftIdSelector}`
  // Backend writes to this path when optimization callback is received (via events.service.ts:emitOptimizingCompleted)
  // We just need to navigate to /orders/new after submitting, and the listener there handles it.

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await request.get("/routeplan");
      setPlans(response || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
      message.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    try {
      await request.delete(`/routeplan/${planId}`);
      message.success("Plan deleted successfully");
      fetchPlans();
    } catch (error) {
      console.error("Error deleting plan:", error);
      message.error("Failed to delete plan");
    }
  };

  const handleViewPlan = (planId) => {
    history.push(`/routeplan/${planId}`);
  };

  const generateRandomJobId = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    let result = "";
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `job_${result}`;
  };

  const validateCoordinates = (lat, lng, addressString) => {
    const DEFAULT_LAT = 18.4134459;
    const DEFAULT_LNG = -66.098316;

    const isInvalid = !lat || !lng || lat === 0 || lng === 0;

    if (isInvalid) {
      console.warn(
        `
        ⚠️ Invalid coordinates for address: ${addressString}. Using default: ${DEFAULT_LAT}, ${DEFAULT_LNG}
        `
      );
      return { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
    }

    return { lat, lng };
  };

  const handleSubmitPlan = async (planId) => {
    try {
      setSubmittingPlanId(planId);
      message.loading({
        content: "Preparing route for optimization...",
        key: "submit-plan"
      });

      // Fetch full plan details with paquetes
      const planDetails = await request.get(`/routeplan/${planId}`);

      if (!planDetails || !planDetails.paquetes || planDetails.paquetes.length === 0) {
        message.error("Cannot submit plan: No packages found");
        setSubmittingPlanId(null);
        return;
      }

      // Build address string helper
      const buildAddressString = (addr) => {
        if (!addr) return "";
        const parts = [
          addr.addressLine1,
          addr.addressLine2,
          addr.city,
          addr.state,
          addr.zipCode
        ].filter(Boolean);
        return parts.join(", ");
      };

      // Extract unique addresses that need geocoding
      const addressesToGeocode = new Map();

      for (const paquete of planDetails.paquetes) {
        const pickupAddr = buildAddressString(paquete.pickupAddress);
        const deliveryAddr = buildAddressString(paquete.deliveryAddress);

        if (pickupAddr && !paquete.pickupAddress?.latitude) {
          addressesToGeocode.set(pickupAddr, { type: "pickup", paquete });
        }
        if (deliveryAddr && !paquete.deliveryAddress?.latitude) {
          addressesToGeocode.set(deliveryAddr, { type: "delivery", paquete });
        }
      }

      // Geocode addresses that don't have coordinates
      message.loading({ content: "Geocoding addresses...", key: "submit-plan" });
      const geocodedMap = {};

      for (const [address, ref] of addressesToGeocode.entries()) {
        try {
          const geocodeResult = await request.get("/geocode", {
            params: { address }
          });

          if (geocodeResult && geocodeResult.lat && geocodeResult.lng) {
            geocodedMap[address] = {
              lat: geocodeResult.lat,
              lng: geocodeResult.lng
            };
          } else {
            console.warn(`Failed to geocode address: ${address}`);
            geocodedMap[address] = { lat: 0, lng: 0 };
          }
        } catch (error) {
          console.error(`Error geocoding ${address}:`, error);
          geocodedMap[address] = { lat: 0, lng: 0 };
        }
      }

      // Build jobs from paquetes
      const formattedJobs = planDetails.paquetes.map((paquete) => {
        const pickupAddrString = buildAddressString(paquete.pickupAddress);
        const deliveryAddrString = buildAddressString(paquete.deliveryAddress);

        const rawPickupCoords = paquete.pickupAddress?.latitude
          ? { lat: paquete.pickupAddress.latitude, lng: paquete.pickupAddress.longitude }
          : geocodedMap[pickupAddrString] || { lat: 0, lng: 0 };

        const rawDeliveryCoords = paquete.deliveryAddress?.latitude
          ? {
              lat: paquete.deliveryAddress.latitude,
              lng: paquete.deliveryAddress.longitude
            }
          : geocodedMap[deliveryAddrString] || { lat: 0, lng: 0 };

        // Validate coordinates with default fallback
        const pickupCoords = validateCoordinates(
          rawPickupCoords.lat,
          rawPickupCoords.lng,
          pickupAddrString
        );
        const deliveryCoords = validateCoordinates(
          rawDeliveryCoords.lat,
          rawDeliveryCoords.lng,
          deliveryAddrString
        );

        const jobId = generateRandomJobId();

        return {
          id: jobId,
          pickup: {
            address: pickupAddrString,
            lat: pickupCoords.lat,
            lng: pickupCoords.lng,
            start: "06:00",
            end: "20:00",
            duration: 10
          },
          dropoff: {
            address: deliveryAddrString,
            lat: deliveryCoords.lat,
            lng: deliveryCoords.lng,
            start: "06:00",
            end: "20:00",
            duration: 10
          },
          packages: [
            {
              id: `${jobId}-package`,
              quantity: 1,
              name: "Small",
              packageTypeId: "1844330d-167d-4dd9-948b-b8a6bee7dd3b",
              maxLength: 12,
              maxWeight: 10,
              maxVolume: 1
            }
          ]
        };
      });

      // Convert jobs to visits format for optimizer
      const visits = dataJobTypeOptimize(formattedJobs);

      // Add tags to each visit
      const clientId = profileCtx?.profile?.clientId;
      if (clientId) {
        Object.keys(visits).forEach((jobId) => {
          visits[jobId].tags = [`client_id_${clientId}`];
        });
      }

      // Build fleet configuration (default to 1 route)
      const numberRoutes = 1;

      // Extract unique pickups and dropoffs for the ConfirmRoutePage
      // Note: Convert time strings to moment objects for form validation
      const pickupsForRedux = formattedJobs.map((job, index) => ({
        id: `pickup-${index}`,
        title: `Pickup Location`,
        position: index + 1,
        address: {
          address: job.pickup.address,
          lngLat: [job.pickup.lng, job.pickup.lat]
        },
        from_time: moment(job.pickup.start, "HH:mm"),
        to_time: moment(job.pickup.end, "HH:mm"),
        duration: job.pickup.duration,
        phone_numbers: [{ type: "contact", phone: "9396521963" }],
        contact_name: "Route Contact"
      }));

      const dropoffsForRedux = formattedJobs.map((job, index) => ({
        id: `dropoff-${index}`,
        title: `Drop-off Location`,
        position: index + 1,
        address: {
          address: job.dropoff.address,
          lngLat: [job.dropoff.lng, job.dropoff.lat]
        },
        from_time: moment(job.dropoff.start, "HH:mm"),
        to_time: moment(job.dropoff.end, "HH:mm"),
        duration: job.dropoff.duration,
        phone_numbers: [{ type: "contact", phone: "9396521963" }],
        contact_name: "Route Contact"
      }));

      // Create packages array that links pickups to dropoffs
      const packagesForRedux = formattedJobs.map((job, index) => ({
        id: `package-${index}`,
        pickupId: `pickup-${index}`,
        packageTypeId: "1844330d-167d-4dd9-948b-b8a6bee7dd3b", // Small package type
        quantity: 1,
        name: "Small",
        type: "Small",
        maxLength: 12,
        maxWeight: 10,
        maxVolume: 1,
        dropoffs: [
          {
            id: `dropoff-${index}`,
            quantity: 1
          }
        ]
      }));

      const orderData = {
        jobs: convertArrayToObject(formattedJobs, "id"),
        pickups: [
          {
            address: {
              address: formattedJobs[0]?.pickup?.address || "",
              lngLat: [
                formattedJobs[0]?.pickup?.lng || 0,
                formattedJobs[0]?.pickup?.lat || 0
              ]
            }
          }
        ],
        clientId: clientId
      };

      let fleet = handleRoutesOptimize(orderData, numberRoutes, []);
      const fleetParam = deleteShiftStart(fleet);

      // Save order data to Redux state (needed by ConfirmRoutePage)
      // Set isConfirmLocations: true to land on Route Optimization step (Step 3)
      // This allows users to see the map and verify stops before final confirmation
      // Use empty string for clientId to match normal flow (backend uses req.user.clientId)
      // Set isOptimizeRoute: true so the backend knows to optimize this route
      // Include planId and fromPlanFlow so we can delete the plan after successful order creation
      dispatch(
        saveAsDraftNewOrder({
          data: {
            ...orderData,
            routes: fleetParam,
            pickups: pickupsForRedux,
            dropoffs: dropoffsForRedux,
            packages: packagesForRedux,
            isOptimizeRoute: true,
            isConfirmLocations: true,
            isConfirmRoute: false,
            clientId: clientId || "",
            planId: planId,
            fromPlanFlow: true
          }
        })
      );

      // Dispatch optimization - generates draftId and submits to backend
      // Backend stores the result, which the confirm page polls at:
      // `/route-job-results/optimizing/${draftId}`
      message.loading({ content: "Submitting for optimization...", key: "submit-plan" });
      const draftId = new Date().getTime();
      dispatch(doStoreDraftId(draftId));

      await dispatch(
        doOptimizeOrder({
          userId: user?.sub,
          draftId,
          visits,
          fleet: fleetParam,
          options: {
            polylines: true,
            balance: true,
            distance_type: "distance_travelled"
          },
          order: {
            ...orderData,
            routes: fleetParam
          }
        })
      );

      // Navigate to confirmation page, which polls for the result
      // The ConfirmRoutePage (/orders/new) will:
      // 1. Poll `/route-job-results/optimizing/${draftId}`
      // 2. Receive the optimization results when backend sends them
      // 3. Display the optimized routes
      message.success({
        content: "Navigating to confirmation page...",
        key: "submit-plan",
        duration: 1
      });

      setTimeout(() => {
        history.push("/orders/new");
      }, 100);
    } catch (error) {
      console.error("Error submitting plan:", error);
      message.error({ content: "Failed to submit plan", key: "submit-plan" });
      setSubmittingPlanId(null);
    }
  };

  const columns = [
    {
      title: "Plan ID",
      dataIndex: "id",
      key: "id",
      render: (id) => shortId(id)
    },
    {
      title: "Packages",
      key: "packages",
      render: (_, record) => record.paquetes?.length || 0
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-")
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleViewPlan(record.id)}
            size="small"
          >
            View
          </Button>
          <Button
            type="default"
            style={{ backgroundColor: "#52c41a", color: "#fff", borderColor: "#52c41a" }}
            icon={<SendOutlined />}
            onClick={() => handleSubmitPlan(record.id)}
            size="small"
            loading={submittingPlanId === record.id}
            disabled={submittingPlanId !== null}
          >
            Submit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this plan?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="danger" icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ];

  return (
    <div className="plan-list-page">
      <RoutesHeader breadCrumbs={breadCrumbs} />
      <div className="content-wrapper">
        <h2 className="page-title">Draft Route Plans</h2>
        <Table
          dataSource={plans}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} plans`
          }}
        />
      </div>
    </div>
  );
};

export default PlanListPage;
